import { Loader2, Pause, Play } from 'lucide-react';
import { DemoLock } from './DemoLock';
import { IS_DEMO } from '../../apiBase';

// The pipeline BUTTONS — "New run" and Pause/Resume. IN THE SIDEBAR (see DocSidebar), over
// the run pager, on every phase: a run is the whole document's, and Pause was a button you
// had to go back to Phase 1's bottom to find while the run was busy on Phase 3.
//
// Not in the demo, which runs nothing (PhaseControls hides every phase's the same way), and
// dead behind DemoLock in the sandbox, which shows what it refuses.
//
// The parent owns all the run state; this only draws and reports the gestures.
// `progressText`/`elapsedText` are pre-formatted by the parent so the
// phase-number/clock logic lives in one place.
export function RunControls({
  numPages,
  runningAll,
  pausedRun,
  pauseRequested,
  progressText,
  elapsedText,
  onRunAll,
  onPauseAll,
  onResumeAll,
  t,
}: {
  numPages: number;
  runningAll: boolean;
  pausedRun: boolean;
  pauseRequested: boolean;
  progressText: string;
  elapsedText: string | null;
  onRunAll: () => void;
  onPauseAll: () => void;
  onResumeAll: () => void;
  t: (k: string) => string;
}) {
  const runMoving = runningAll && !pausedRun;
  if (IS_DEMO) return null;
  return (
    // One button to a line: the rail is 200px inside, and the label with its progress and
    // clock is most of that.
    <div className="mt-4 flex flex-col gap-2 border-t border-slate-600 pt-4">
      <DemoLock>
        {/* Live only while calls are actually going out. A SUSPENDED run leaves this
            enabled, and that is the way to abandon one: it already means "from
            scratch" — clear every phase, open a new Phase 1 run — so beside Reprendre
            it reads as the choice it is. Its readouts (spinner, "· 2/3", clock) belong
            to the run in flight, so they go while it is held. */}
        <button
          type="button"
          onClick={onRunAll}
          disabled={runMoving || numPages < 1}
          className="flex w-full flex-wrap items-center justify-center gap-x-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-center text-sm font-semibold text-slate-800 shadow-sm transition-colors hover:border-blue-500 hover:bg-blue-50 disabled:opacity-50"
        >
          {/* A spinner while the chain runs, and nothing at rest: the label already
              says what the button does, and a star said only that something would
              happen. */}
          {runMoving && <Loader2 className="h-4 w-4 animate-spin" />}
          {t('notice.runAll')}
          {progressText}
          {elapsedText != null && (
            <span className="tabular-nums font-normal text-slate-400">
              {elapsedText}
            </span>
          )}
        </button>

        {/* One button, two jobs: hold the run, then let it go on. It replaces itself
            rather than sitting next to a twin. There is no Stop — Stop only differed
            by aborting calls in flight, which cancels nothing (Gemini bills a sent
            call regardless) and threw away answers Pause keeps. */}
        {runMoving && (
          <button
            type="button"
            onClick={onPauseAll}
            disabled={pauseRequested}
            title={t('notice.pauseHint')}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:border-blue-500 hover:bg-blue-50 disabled:opacity-50"
          >
            <Pause className="h-4 w-4" />
            {pauseRequested ? t('notice.pausing') : t('notice.pause')}
          </button>
        )}
        {pausedRun && (
          <button
            type="button"
            onClick={onResumeAll}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-blue-500 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 shadow-sm transition-colors hover:bg-blue-100"
          >
            <Play className="h-4 w-4" />
            {t('notice.resume')}
          </button>
        )}

      </DemoLock>
    </div>
  );
}
